import { useId, type ReactNode } from 'react';
import { IconChevronRight } from '@tabler/icons-react';

import { useTranslation } from '../../i18n/LocaleProvider';
import { Panel, type PanelProps } from '../core/Panel';

/** The numbers behind the picture, rendered as a real table. */
export interface ChartTable {
  /** The table's accessible name. */
  caption: string;
  columns: string[];
  /** Cells are already locale-formatted strings, or plain numbers. */
  rows: Array<Array<string | number>>;
}

export interface ChartFrameProps extends Omit<PanelProps, 'children' | 'footer' | 'headerActions'> {
  title: ReactNode;
  description?: ReactNode;
  /**
   * One or two sentences describing what the chart shows. Rendered visually
   * hidden and wired to the chart with `aria-describedby`, so the picture is
   * never the only way to read the numbers.
   */
  summary: string;
  /**
   * The chart's accessible name. Required unless `chartRole` is `'none'`, which
   * is the case for a chart that carries its own semantics (the radial gauge).
   */
  chartLabel?: string;
  /** `'img'` for a decorative chart, `'none'` when the child labels itself. */
  chartRole?: 'img' | 'none';
  /** Overrides the id of the hidden summary, so a child can point at it too. */
  summaryId?: string;
  legend?: ReactNode;
  /** Rendered top-right of the header — buttons, menus, a range switcher. */
  toolbar?: ReactNode;
  table?: ChartTable;
  /** Height of the plot area in px. */
  height?: number;
  /**
   * When set, the plot is replaced by this message. An interactive legend can
   * switch every series off, so that state is reachable and must be explained
   * rather than left as a blank grid. The summary and the data table stay
   * either way, so the numbers are never lost with the picture.
   */
  empty?: string;
  children: ReactNode;
}

function ChartDataTable({ table }: { table: ChartTable }) {
  const { t } = useTranslation();

  return (
    <details className="group text-sm">
      {/* An explicit chevron rather than the user agent's `::marker`: the
          marker only renders while `summary` keeps its default `list-item`
          display, and this control needs `flex` to hold a 44px target. */}
      <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-2 font-display text-2xs font-semibold uppercase tracking-label text-body focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus [&::-webkit-details-marker]:hidden">
        <IconChevronRight
          aria-hidden="true"
          className="size-4 shrink-0 transition-transform duration-[var(--dur-fast)] ease-spring group-open:rotate-90"
        />
        {t('ds.chart.dataTable')}
      </summary>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-left text-sm">
          <caption className="pb-2 text-left text-xs text-muted">{table.caption}</caption>
          <thead>
            <tr className="border-b border-line-strong">
              {table.columns.map((column, index) => (
                <th
                  key={index}
                  scope="col"
                  className="py-2 pr-4 font-display text-2xs font-semibold uppercase tracking-label text-muted"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b border-line-subtle">
                {row.map((cell, cellIndex) =>
                  cellIndex === 0 ? (
                    <th key={cellIndex} scope="row" className="py-2 pr-4 font-normal text-body">
                      {cell}
                    </th>
                  ) : (
                    <td key={cellIndex} className="py-2 pr-4 tabular-nums text-heading">
                      {cell}
                    </td>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * The wrapper every chart in this family composes: a Panel with the title and
 * description, an optional legend row, an optional toolbar, a visually hidden
 * textual summary, and the data-table equivalent of the plotted numbers.
 */
export function ChartFrame({
  title,
  description,
  summary,
  summaryId,
  chartLabel,
  chartRole = 'img',
  legend,
  toolbar,
  table,
  height = 320,
  empty,
  children,
  ...rest
}: ChartFrameProps) {
  const generatedId = useId();
  const summaryTextId = summaryId ?? generatedId;
  const isImage = chartRole === 'img';

  return (
    <Panel
      {...rest}
      title={title}
      description={description}
      headerActions={toolbar}
      footer={table ? <ChartDataTable table={table} /> : undefined}
    >
      <p id={summaryTextId} className="dhd-sr-only">
        {summary}
      </p>

      {empty ? (
        <p className="grid place-items-center px-6 text-center text-sm text-muted" style={{ height }}>
          {empty}
        </p>
      ) : (
        <div
          role={isImage ? 'img' : undefined}
          aria-label={isImage ? chartLabel : undefined}
          aria-describedby={isImage ? summaryTextId : undefined}
          className="relative w-full"
          style={{ height }}
        >
          {children}
        </div>
      )}

      {legend ? <div className="mt-4">{legend}</div> : null}
    </Panel>
  );
}
